import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Table from '../../../components/common/table/table';
import { INTERNAL_AUDIT_LOGS_VIEW } from '../../../router/routes';
import { useService } from '../../../services/services';
import css from './index.module.css';
import Title from '../../../components/common/title';
import Audit from '../../../interfaces/audit';
import FieldTypes from '../../../enums/field-types';
import { DefaultFilter } from '../../../components/common/table/default-filter';
import User from '../../../interfaces/user';
import { Option } from '../../../components/form/elements/select';

export default function AuditLogs() {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.internal.audits.'.concat(key)),
    [t]
  );
  const { auditService } = useService();
  const [userFilterValues, setUserFilterValues] = useState([]);
  const [eventFilterValues, setEventFilterValues] = useState([]);
  const [typeFilterValues, setTypeFilterValues] = useState([]);

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      auditService.index({ page, sort, desc, ...filter }),
    [auditService]
  );

  useEffect(() => {
    const subscription = auditService
      .initiatorUsers()
      .subscribe((users: User[]) => {
        let filterValues: Option[] = users.map((user) => {
          return { label: user.name, value: user.id };
        });

        filterValues.sort((a, b) => a.label.localeCompare(b.label));

        setUserFilterValues(filterValues);
      });

    return () => subscription.unsubscribe();
  }, [auditService]);

  useEffect(() => {
    const subscription = auditService
      .availableEvents()
      .subscribe((events: any[]) => {
        let filterValues: Option[] = events.map((event) => {
          return { label: event, value: event };
        });

        filterValues.sort((a, b) => a.label.localeCompare(b.label));

        setEventFilterValues(filterValues);
      });

    return () => subscription.unsubscribe();
  }, [auditService]);

  useEffect(() => {
    const subscription = auditService
      .availableAuditableTypes()
      .subscribe((types: any[]) => {
        let filterValues: Option[] = types.map((type) => {
          return { label: type, value: type };
        });

        filterValues.sort((a, b) => a.label.localeCompare(b.label));

        setTypeFilterValues(filterValues);
      });

    return () => subscription.unsubscribe();
  }, [auditService]);

  return (
    <div className={css['index']}>
      <Title title={t('Audit logs')} />
      <div className={css['body']}>
        <div className={css['toolbar']}></div>
        <Table<Audit>
          request={request}
          keyAccessor="id"
          columns={[
            {
              accessor: 'user.name',
              header: tp('user'),
              filter: (
                <DefaultFilter options={userFilterValues} name="user_id" />
              ),
            },
            {
              accessor: 'event',
              header: tp('event'),
              filter: (
                <DefaultFilter options={eventFilterValues} name="event" />
              ),
            },
            {
              accessor: 'created_at',
              header: tp('created_at'),
              dataType: FieldTypes.DATE_TIME,
              sortable: true,
            },
            {
              accessor: 'auditable_type',
              header: tp('auditable_type'),
              filter: (
                <DefaultFilter
                  options={typeFilterValues}
                  name="auditableType"
                />
              ),
            },
            {
              accessor: 'old_values',
              header: tp('old_values'),
              dataType: FieldTypes.OBJECT,
            },
            {
              accessor: 'new_values',
              header: tp('new_values'),
              dataType: FieldTypes.OBJECT,
            },
            {
              accessor: 'url',
              header: tp('url'),
            },
            {
              accessor: 'ip_address',
              header: tp('ip_address'),
            },
            {
              accessor: 'user_agent',
              header: tp('user_agent'),
            },
          ]}
          viewRoute={INTERNAL_AUDIT_LOGS_VIEW}
          viewRecordAbility={{ title: 'View audits' }}
        />
      </div>
    </div>
  );
}
